import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiUser, FiPhone, FiMail, FiMessageSquare, FiInbox, FiGlobe, FiSmartphone } from 'react-icons/fi';
import './AdminPages.css';

const UserWebContactList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const partnerPath = window.location.pathname.startsWith('/partner');
    setIsAdmin(!partnerPath);
  }, []);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/user-web-contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to load contact submissions');
      toast.error('Failed to fetch user web contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    if (!isAdmin) return;
    setUpdatingId(id);
    try {
      const token = getAuthToken();
      await axios.put(
        `${API_URL}/admin/user-web-contacts/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Marked as ${status}`);
      fetchRows();
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      replied: { bg: '#d1fae5', color: '#065f46' },
      closed: { bg: '#f1f5f9', color: '#475569' },
    };
    const s = map[status] || map.pending;
    return (
      <span
        style={{
          padding: '0.25rem 0.625rem',
          borderRadius: '1rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: s.bg,
          color: s.color,
        }}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>User Web Contact List</h1>
        </div>
        <div className="admin-card">
          <div className="skeleton" style={{ height: '400px', borderRadius: '8px' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>User Web Contact List</h1>
        </div>
        <div className="admin-card empty-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>User Web Contact List</h1>
        <p style={{ color: 'var(--admin-text-muted)', marginTop: '0.35rem', fontSize: '0.9rem' }}>
          Messages from the website contact form and the mobile app &ldquo;Contact Us&rdquo; screen.
        </p>
      </div>

      <div className="admin-card">
        {rows.length === 0 ? (
          <div className="empty-state" style={{ padding: '3rem', textAlign: 'center' }}>
            <FiInbox size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <h3>No submissions yet</h3>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Message</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Date</th>
                  {isAdmin ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>
                      <span className="cell-with-icon">
                        <FiUser /> {r.name}
                      </span>
                    </td>
                    <td>
                      <span className="cell-with-icon">
                        <FiPhone /> {r.phone || '—'}
                      </span>
                    </td>
                    <td>
                      <span className="cell-with-icon">
                        <FiMail /> {r.email || '—'}
                      </span>
                    </td>
                    <td style={{ maxWidth: '220px', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                      <FiMessageSquare style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.5 }} />
                      {r.message || '—'}
                    </td>
                    <td>
                      {r.source === 'mobile' ? (
                        <span className="cell-with-icon">
                          <FiSmartphone /> App
                        </span>
                      ) : (
                        <span className="cell-with-icon">
                          <FiGlobe /> Web
                        </span>
                      )}
                    </td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                    </td>
                    {isAdmin ? (
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn-sm btn-secondary"
                            disabled={updatingId === r.id || r.status === 'replied'}
                            onClick={() => handleStatusUpdate(r.id, 'replied')}
                          >
                            Replied
                          </button>
                          <button
                            type="button"
                            className="btn-sm btn-secondary"
                            disabled={updatingId === r.id || r.status === 'closed'}
                            onClick={() => handleStatusUpdate(r.id, 'closed')}
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserWebContactList;
